import { TierInfo } from './types';

export const FOUNDING_MEMBER_SPOTS = 10;
// FOUNDING_MEMBER_SPOTS_TAKEN is now dynamic — fetched from Firestore customers collection
// This constant is only used as a fallback when the API isn't available
export const FOUNDING_MEMBER_SPOTS_TAKEN = 0;
export const FOUNDING_MEMBER_PRICE = 49;

// Annual billing = 2 months free (pay for 10 months)
export const ANNUAL_PRICES: Record<string, { monthly: number; annual: number; foundingAnnual?: number }> = {
  iron: { monthly: 99, annual: 990, foundingAnnual: 490 },
  force: { monthly: 199, annual: 1990, foundingAnnual: 990 },
  summit: { monthly: 349, annual: 3490, foundingAnnual: 1740 },
};

export const TIERS: TierInfo[] = [
  {
    id: 'iron',
    name: 'Iron',
    tagline: 'Never wait on manufacturer support again.',
    price: 99,
    foundingPrice: 49,
    highlighted: true,
    features: [
      {
        icon: 'cpu',
        title: 'AI Repair Assistant',
        description: 'Type your error code, get a real answer: voltages, ohm readings, part numbers. Powered by AthnxTech.',
      },
      {
        icon: 'calendar',
        title: 'Preventative Maintenance Board',
        description: 'Generated from your equipment inventory. Task board with supplies needed, due dates, and progress tracking.',
      },
      {
        icon: 'activity',
        title: 'Equipment Dashboard',
        description: 'Every machine on your floor, one view. Upload photos, change status, track repairs over time.',
      },
      {
        icon: 'calculator',
        title: 'Replace vs. Repair Calculator',
        description: 'Accounts for repair cost, used market value, installation cost, and disposal cost.',
      },
      {
        icon: 'shield',
        title: 'Manufacturer Playbook',
        description: 'Life Fitness, Precor, Technogym, Matrix, Cybex, Star Trac — how to get what you need from each one.',
      },
      {
        icon: 'package',
        title: 'Parts & Supplies',
        description: 'Auto-generated list of parts and supplies to stock based on your equipment. Never get caught without the right belt or cable.',
      },
      {
        icon: 'message-circle',
        title: 'Async Support',
        description: 'Submit a photo or video of the issue, get a same-day response. Hours, not days.',
      },
    ],
  },
  {
    id: 'force',
    name: 'Force',
    tagline: 'Run your equipment AND your team.',
    price: 199,
    foundingPrice: 99,
    features: [
      {
        icon: 'clipboard-check',
        title: 'Everything in Iron',
        description: 'All Iron tier features included.',
      },
      {
        icon: 'book-open',
        title: 'Parts & Diagram Guides',
        description: 'How to find the right diagram for any unit — including discontinued. Where to source parts.',
      },
      {
        icon: 'library',
        title: 'Equipment Knowledge Base',
        description: 'Upload PDFs, manuals, service bulletins for your equipment. Accessible from anywhere, tagged by machine, searchable.',
      },
      {
        icon: 'users',
        title: 'Team Performance Suite',
        description: 'Staff scorecards, team leaderboards, monthly goals, and reward tracking — one unified dashboard for running your team.',
      },
      {
        icon: 'phone',
        title: 'Monthly Q&A Call',
        description: '30-minute focused call with pre-call form. Direct access to someone who actually knows the equipment.',
      },
    ],
  },
  {
    id: 'summit',
    name: 'Summit',
    tagline: 'The complete gym operating system.',
    price: 349,
    foundingPrice: 174,
    features: [
      {
        icon: 'clipboard-check',
        title: 'Everything in Iron + Force',
        description: 'All Iron and Force tier features included.',
      },
      {
        icon: 'globe',
        title: 'Custom Website w/ Analytics Board',
        description: '$0 build fee, 2 updates/month + analytics dashboard included. Built by AthnxWeb.',
      },
      {
        icon: 'dumbbell',
        title: 'AI Member Programming Engine',
        description: 'Generate personalized strength + cardio programs in seconds. Trainers save 30–60 min per client per month — members get tailored plans on demand.',
      },
      {
        icon: 'percent',
        title: <>20% <a href="https://athnxbuild.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">AthnxBuild</a> Discount</>,
        description: 'Send in motors, rollers, or cables and save on every rebuild.',
      },
      {
        icon: 'search',
        title: 'Quarterly Gym Audit',
        description: '30-minute focused meeting + 3 written priority action items delivered.',
      },
      {
        icon: 'zap',
        title: 'Priority Async Support',
        description: 'Response within hours, not same-day. Your issues come first.',
      },
    ],
  },
];

export const VALUE_COMPARISON = [
  { item: 'Average service call', cost: '$200–$600', note: 'For a 20-minute fix you could do yourself' },
  { item: 'Manufacturer hold time', cost: '40+ min', note: 'Just to talk to someone reading a script' },
  { item: 'Equipment downtime', cost: '2+ weeks', note: 'Average turnaround for a service company' },
  { item: 'Lost member (1-star review)', cost: '$600/yr', note: 'One member at $50/month gone forever' },
  { item: 'Preventable breakdowns/year', cost: '$1,600+', note: '4-6 incidents at $400+ each' },
];

export const PAIN_POINTS = [
  {
    title: 'Equipment Down',
    description: 'Treadmill goes down Friday afternoon. Tech can\'t come until Thursday. Members are pissed. Two 1-star reviews drop over the weekend.',
    icon: 'alert-triangle',
  },
  {
    title: 'Useless Support',
    description: 'Called the manufacturer. Waited on hold 40 minutes. Got transferred twice. Someone read from a script. Still no fix.',
    icon: 'phone-off',
  },
  {
    title: 'Expensive Techs',
    description: '$200–$600 just for a diagnostic visit. Tech is there 30 minutes. Fixes something you could have handled in 20 — if someone had just told you the trick.',
    icon: 'dollar-sign',
  },
];
